import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface AttendanceHistoryPoint {
  period_label: string;
  period_start: string;
  attendance_count: number;
}

export function useStudentAttendanceHistory(
  studentId: string | null,
  period: 'week' | 'month' | 'quarter' | 'year' = 'month',
  sportId: string | null = null
) {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: ['student-attendance-history', studentId, companyId, period, sportId],
    queryFn: async () => {
      if (!companyId || !studentId) return [];

      try {
        const { data, error } = await supabase.rpc('get_student_attendance_history' as any, {
          p_student_id: studentId,
          p_company_id: companyId,
          p_period: period,
          p_sport_id: sportId || null,
        });

        if (error) {
          // If function doesn't exist (migration not applied), return empty array gracefully
          if (error.code === 'PGRST202' || error.message?.includes('does not exist') || error.message?.includes('function')) {
            console.warn('get_student_attendance_history function not found. Migrations may not be applied.');
            return [];
          }
          console.error('Error fetching student attendance history:', error);
          throw error;
        }

        return (data || []) as AttendanceHistoryPoint[];
      } catch (error: any) {
        // Handle function not found gracefully
        if (error?.code === 'PGRST202' || error?.message?.includes('does not exist') || error?.message?.includes('function')) {
          console.warn('get_student_attendance_history function not found. Migrations may not be applied.');
          return [];
        }
        throw error;
      }
    },
    enabled: !!companyId && !!studentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
